import { collection, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

// Reset all player stats to zero
export const resetPlayerStats = async () => {
  try {
    console.log('Starting player stats reset...');
    
    // Get all players
    const playersSnapshot = await getDocs(collection(db, 'players'));
    const players = playersSnapshot.docs;
    
    console.log(`Found ${players.length} players to reset`);
    
    // Reset each player's stats
    const resetPromises = players.map(async (playerDoc) => {
      const playerData = playerDoc.data();
      
      // Reset all stats to 0
      const resetStats = {
        matchesPlayed: 0,
        matchesWon: 0,
        matchesLost: 0,
        setsWon: 0,
        setsLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        pointsWon: 0,
        pointsLost: 0,
        aces: 0,
        doubleFaults: 0,
        winners: 0,
        unforcedErrors: 0,
        sportsmanshipRating: 5,
        totalRating: 0,
        averageRating: 0,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(doc(db, 'players', playerDoc.id), resetStats);
      console.log(`Reset stats for player: ${playerData.name}`);
    });
    
    await Promise.all(resetPromises);
    console.log('✅ All player stats have been reset successfully!');
    return { success: true, message: `Reset stats for ${players.length} players` };
    
  } catch (error) {
    console.error('❌ Error resetting player stats:', error);
    return { success: false, error: error.message };
  }
};

// Delete all leagues
export const deleteAllLeagues = async () => {
  try {
    console.log('Starting league deletion...');
    
    // Get all leagues
    const leaguesSnapshot = await getDocs(collection(db, 'leagues'));
    const leagues = leaguesSnapshot.docs;
    
    console.log(`Found ${leagues.length} leagues to delete`);
    
    // Delete each league
    const deletePromises = leagues.map(async (leagueDoc) => {
      await deleteDoc(doc(db, 'leagues', leagueDoc.id));
      console.log(`Deleted league: ${leagueDoc.data().name}`);
    });
    
    await Promise.all(deletePromises);
    console.log('✅ All leagues have been deleted successfully!');
    return { success: true, message: `Deleted ${leagues.length} leagues` };
    
  } catch (error) {
    console.error('❌ Error deleting leagues:', error);
    return { success: false, error: error.message };
  }
};

// Delete all fixtures
export const deleteAllFixtures = async () => {
  try {
    console.log('Starting fixture deletion...');
    
    // Get all fixtures
    const fixturesSnapshot = await getDocs(collection(db, 'fixtures'));
    const fixtures = fixturesSnapshot.docs;
    
    console.log(`Found ${fixtures.length} fixtures to delete`);
    
    // Delete each fixture
    const deletePromises = fixtures.map(async (fixtureDoc) => {
      await deleteDoc(doc(db, 'fixtures', fixtureDoc.id));
      console.log(`Deleted fixture: ${fixtureDoc.id}`);
    });
    
    await Promise.all(deletePromises);
    console.log('✅ All fixtures have been deleted successfully!');
    return { success: true, message: `Deleted ${fixtures.length} fixtures` };
    
  } catch (error) {
    console.error('❌ Error deleting fixtures:', error);
    return { success: false, error: error.message };
  }
};

// Complete reset for live preparation
export const prepareForLive = async () => {
  try {
    console.log('🚀 Starting live preparation...');
    
    // Reset player stats
    const statsResult = await resetPlayerStats();
    if (!statsResult.success) {
      throw new Error(`Failed to reset player stats: ${statsResult.error}`);
    }
    
    // Delete all leagues
    const leaguesResult = await deleteAllLeagues();
    if (!leaguesResult.success) {
      throw new Error(`Failed to delete leagues: ${leaguesResult.error}`);
    }
    
    // Delete all fixtures
    const fixturesResult = await deleteAllFixtures();
    if (!fixturesResult.success) {
      throw new Error(`Failed to delete fixtures: ${fixturesResult.error}`);
    }
    
    console.log('🎉 Live preparation completed successfully!');
    return { 
      success: true, 
      message: 'Live preparation completed successfully!',
      details: {
        statsReset: statsResult.message,
        leaguesDeleted: leaguesResult.message,
        fixturesDeleted: fixturesResult.message
      }
    };
    
  } catch (error) {
    console.error('❌ Error during live preparation:', error);
    return { success: false, error: error.message };
  }
};

// Individual functions for specific resets
export const resetAllData = {
  resetPlayerStats,
  deleteAllLeagues,
  deleteAllFixtures,
  prepareForLive
};
